/**
 * Cookie disclosure scanner
 *
 * Shows only cookies currently readable on this visit (document.cookie),
 * enriched with known metadata for Shopify, Clarity, GA, Meta, etc.
 * Optionally can also list Clarity cookies when the Clarity script is present.
 */
(function () {
  const KNOWLEDGE = {
    shopify_necessary: {
      id: 'shopify_necessary',
      label: 'Shopify',
      category: 'necessary',
      detect: () => true,
      cookies: [
        {
          name: '_shopify_essential',
          provider: 'Shopify',
          purpose:
            'Contains essential information for the correct functioning of the storefront, such as session state and anti-tampering data.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: '_shopify_test',
          provider: 'Shopify',
          purpose: 'Used to check whether the browser supports cookies.',
          expiry: '1 minute',
          type: 'HTTP Cookie',
        },
        {
          name: '_tracking_consent',
          provider: 'Shopify',
          purpose:
            'Stores your cookie and tracking consent preferences when privacy rules apply in your region.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: 'secure_customer_sig',
          provider: 'Shopify',
          purpose:
            'Used to identify a customer after they have logged in to a customer account, if accounts are enabled.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: 'storefront_digest',
          provider: 'Shopify',
          purpose:
            'Stores a digest of the storefront password so a password-protected storefront can be previewed.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: '_secure_session_id',
          provider: 'Shopify',
          purpose: 'Used in connection with navigation through the storefront.',
          expiry: '24 hours',
          type: 'HTTP Cookie',
        },
      ],
    },
    shopify_preferences: {
      id: 'shopify_preferences',
      label: 'Shopify',
      category: 'preferences',
      detect: () => true,
      cookies: [
        {
          name: 'localization',
          provider: 'Shopify',
          purpose:
            'Used to localise the storefront to the correct country, region, or language when Markets or localisation features are used.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: 'keep_alive',
          provider: 'Shopify',
          purpose: 'Used in connection with buyer localisation.',
          expiry: 'Session',
          type: 'HTTP Cookie',
        },
      ],
    },
    shopify_statistics: {
      id: 'shopify_statistics',
      label: 'Shopify',
      category: 'statistics',
      detect: () =>
        hasScript(/shopifycloud\.com|cdn\.shopify\.com.*analytics|trekkie|shop_events_listener/i) ||
        Boolean(window.ShopifyAnalytics) ||
        hasCookieName(/_shopify_s|_shopify_y|_shopify_sa_/),
      cookies: [
        {
          name: '_shopify_s',
          provider: 'Shopify',
          purpose:
            'Used by Shopify analytics to collect information about how visitors interact with the storefront.',
          expiry: '30 minutes',
          type: 'HTTP Cookie',
        },
        {
          name: '_shopify_y',
          provider: 'Shopify',
          purpose:
            'Used by Shopify analytics to recognise returning visitors and measure storefront performance.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: '_shopify_sa_t',
          provider: 'Shopify',
          purpose:
            'Used by Shopify analytics for marketing-attribution related to landing pages.',
          expiry: '30 minutes',
          type: 'HTTP Cookie',
        },
        {
          name: '_shopify_sa_p',
          provider: 'Shopify',
          purpose:
            'Used by Shopify analytics for marketing-attribution related to landing pages.',
          expiry: '30 minutes',
          type: 'HTTP Cookie',
        },
      ],
    },
    clarity: {
      id: 'clarity',
      label: 'Microsoft Clarity',
      category: 'statistics',
      detect: () =>
        typeof window.clarity === 'function' ||
        hasScript(/clarity\.ms|clarity\.js/i) ||
        hasCookieName(/_clck|_clsk|^CLID$/),
      cookies: [
        {
          name: '_clck',
          provider: 'Microsoft Clarity',
          purpose:
            'Persists the Microsoft Clarity user ID and preferences so returning visitors on this site can be attributed to the same ID.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: '_clsk',
          provider: 'Microsoft Clarity',
          purpose:
            'Connects multiple page views by a visitor into a single Microsoft Clarity session recording.',
          expiry: '1 day',
          type: 'HTTP Cookie',
        },
        {
          name: 'CLID',
          provider: 'Microsoft Clarity',
          purpose:
            'Identifies the first time Microsoft Clarity saw this user on any site that uses Clarity.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
      ],
    },
    clarity_microsoft: {
      id: 'clarity_microsoft',
      label: 'Microsoft',
      category: 'marketing',
      detect: () =>
        typeof window.clarity === 'function' ||
        hasScript(/clarity\.ms|clarity\.js|bat\.bing\.com|c\.bing\.com/i) ||
        hasCookieName(/ANONCHK|^MR$|^MUID$|^SM$/),
      cookies: [
        {
          name: 'ANONCHK',
          provider: 'Microsoft',
          purpose:
            'Indicates whether the Microsoft browser ID (MUID) is transferred to advertising cookies. Clarity sets this to 0 and does not use ANID.',
          expiry: '10 minutes',
          type: 'HTTP Cookie',
        },
        {
          name: 'MR',
          provider: 'Microsoft',
          purpose: 'Indicates whether Microsoft should refresh the MUID browser identifier.',
          expiry: '7 days',
          type: 'HTTP Cookie',
        },
        {
          name: 'MUID',
          provider: 'Microsoft',
          purpose:
            'Identifies unique browsers visiting Microsoft sites. Used for advertising, analytics, and related operational purposes.',
          expiry: '1 year',
          type: 'HTTP Cookie',
        },
        {
          name: 'SM',
          provider: 'Microsoft',
          purpose: 'Used to synchronise the Microsoft MUID across Microsoft domains.',
          expiry: 'Session',
          type: 'HTTP Cookie',
        },
      ],
    },
    google_analytics: {
      id: 'google_analytics',
      label: 'Google Analytics',
      category: 'statistics',
      detect: () =>
        typeof window.gtag === 'function' ||
        typeof window.ga === 'function' ||
        Boolean(window.dataLayer) ||
        hasScript(/googletagmanager\.com|google-analytics\.com|gtag\/js/i) ||
        hasCookieName(/^_ga|^_gid|^_gat/),
      cookies: [
        {
          name: '_ga',
          provider: 'Google',
          purpose: 'Distinguishes users for Google Analytics.',
          expiry: '2 years',
          type: 'HTTP Cookie',
        },
        {
          name: '_ga_*',
          provider: 'Google',
          purpose: 'Persists Google Analytics 4 session state for a property.',
          expiry: '2 years',
          type: 'HTTP Cookie',
        },
        {
          name: '_gid',
          provider: 'Google',
          purpose: 'Distinguishes users for Google Analytics.',
          expiry: '24 hours',
          type: 'HTTP Cookie',
        },
        {
          name: '_gat',
          provider: 'Google',
          purpose: 'Used to throttle Google Analytics request rate.',
          expiry: '1 minute',
          type: 'HTTP Cookie',
        },
      ],
    },
    meta_pixel: {
      id: 'meta_pixel',
      label: 'Meta Pixel',
      category: 'marketing',
      detect: () =>
        typeof window.fbq === 'function' ||
        hasScript(/connect\.facebook\.net|fbevents\.js/i) ||
        hasCookieName(/^_fbp$|^_fbc$/),
      cookies: [
        {
          name: '_fbp',
          provider: 'Meta',
          purpose: 'Used by Meta to deliver advertising products and measure ads.',
          expiry: '3 months',
          type: 'HTTP Cookie',
        },
        {
          name: '_fbc',
          provider: 'Meta',
          purpose: 'Stores the last Meta click ID when a user arrives from an ad.',
          expiry: '3 months',
          type: 'HTTP Cookie',
        },
      ],
    },
  };

  const CATEGORY_ORDER = ['necessary', 'preferences', 'statistics', 'marketing'];

  function hasScript(pattern) {
    return Array.from(document.scripts).some((script) => {
      const src = script.src || '';
      const inline = script.textContent || '';
      return pattern.test(src) || pattern.test(inline);
    });
  }

  function readBrowserCookies() {
    if (!document.cookie) return [];
    return document.cookie
      .split(';')
      .map((part) => part.trim())
      .filter(Boolean)
      .map((part) => part.split('=')[0].trim())
      .filter(Boolean);
  }

  function hasCookieName(pattern) {
    return readBrowserCookies().some((name) => pattern.test(name));
  }

  function escapeHtml(value) {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  function uniqueCookies(list) {
    const seen = new Set();
    return list.filter((cookie) => {
      const key = `${cookie.name}::${cookie.provider}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }

  function readConfig(root) {
    const configId = root.getAttribute('data-config');
    const node = configId ? document.getElementById(configId) : null;
    if (!node) {
      return {
        showPreferences: true,
        showStatistics: true,
        showMarketing: true,
        labels: {},
        categories: {},
      };
    }

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.warn('[cookie-scanner] invalid config', error);
      return {
        showPreferences: true,
        showStatistics: true,
        showMarketing: true,
        labels: {},
        categories: {},
      };
    }
  }

  function buildKnowledgeIndex() {
    const index = [];
    Object.values(KNOWLEDGE).forEach((vendor) => {
      vendor.cookies.forEach((cookie) => {
        index.push({
          ...cookie,
          category: vendor.category,
          vendorId: vendor.id,
          prefix: cookie.name.endsWith('*') ? cookie.name.slice(0, -1) : null,
        });
      });
    });
    return index;
  }

  const KNOWLEDGE_INDEX = buildKnowledgeIndex();

  function matchKnownCookie(name) {
    return (
      KNOWLEDGE_INDEX.find((entry) => {
        if (entry.prefix) return name.startsWith(entry.prefix);
        return entry.name === name;
      }) || null
    );
  }

  function collectCookies(config) {
    const enabled = {
      necessary: true,
      preferences: config.showPreferences !== false,
      statistics: config.showStatistics !== false,
      marketing: config.showMarketing !== false,
    };

    const byCategory = {
      necessary: [],
      preferences: [],
      statistics: [],
      marketing: [],
    };

    const labels = config.labels || {};
    const presentNames = readBrowserCookies();
    const presentSet = new Set(presentNames);

    presentNames.forEach((name) => {
      const known = matchKnownCookie(name);
      if (known) {
        if (!enabled[known.category]) return;
        byCategory[known.category].push({
          name,
          provider: known.provider,
          purpose: known.purpose,
          expiry: known.expiry,
          type: known.type,
        });
        return;
      }

      if (!enabled.statistics) return;
      byCategory.statistics.push({
        name,
        provider: labels.detected_provider || 'Detected on this device',
        purpose:
          labels.detected_purpose ||
          'This cookie was found in your browser on this visit. Its purpose may depend on an app or script loaded on the site.',
        expiry: labels.detected_expiry || 'Unknown',
        type: 'HTTP Cookie',
      });
    });

    // Optional: if Clarity is installed/loaded, also disclose its known cookies
    // even when consent has not set them on this visit yet.
    const forceVendors = config.forceVendors || {};
    Object.values(KNOWLEDGE).forEach((vendor) => {
      if (!forceVendors[vendor.id]) return;
      if (!enabled[vendor.category]) return;
      if (!vendor.detect()) return;

      vendor.cookies.forEach((cookie) => {
        if (presentSet.has(cookie.name)) return;
        byCategory[vendor.category].push({ ...cookie });
      });
    });

    CATEGORY_ORDER.forEach((category) => {
      byCategory[category] = uniqueCookies(byCategory[category]);
    });

    return byCategory;
  }

  function render(root, config, byCategory) {
    const labels = config.labels || {};
    const groups = root.querySelector('[data-cookie-groups]');
    const scanning = root.querySelector('[data-cookie-scanning]');
    if (!groups) return;

    const categoryMeta = config.categories || {};
    let html = '';

    CATEGORY_ORDER.forEach((category) => {
      const rows = byCategory[category] || [];
      if (!rows.length) return;

      const meta = categoryMeta[category] || {};
      html += `
        <section class="cookie-list__group" aria-labelledby="CookieCategory-${root.id}-${category}">
          <div class="cookie-list__group-header">
            <h3 class="cookie-list__group-title" id="CookieCategory-${root.id}-${category}">
              ${escapeHtml(meta.title || category)}
            </h3>
            <p class="cookie-list__group-desc">${escapeHtml(meta.description || '')}</p>
          </div>
          <div class="cookie-list__table-wrap">
            <table class="cookie-list__table">
              <caption class="visually-hidden">
                ${escapeHtml(meta.title || category)} — ${escapeHtml(labels.table_caption || 'Cookie details')}
              </caption>
              <thead>
                <tr>
                  <th scope="col">${escapeHtml(labels.name || 'Name')}</th>
                  <th scope="col">${escapeHtml(labels.provider || 'Provider')}</th>
                  <th scope="col">${escapeHtml(labels.purpose || 'Purpose')}</th>
                  <th scope="col">${escapeHtml(labels.expiry || 'Expiry')}</th>
                  <th scope="col">${escapeHtml(labels.type || 'Type')}</th>
                </tr>
              </thead>
              <tbody>
                ${rows
                  .map(
                    (cookie) => `
                  <tr>
                    <th scope="row" class="cookie-list__cell cookie-list__cell--name">${escapeHtml(cookie.name)}</th>
                    <td class="cookie-list__cell">${escapeHtml(cookie.provider)}</td>
                    <td class="cookie-list__cell cookie-list__cell--purpose">${escapeHtml(cookie.purpose)}</td>
                    <td class="cookie-list__cell">${escapeHtml(cookie.expiry)}</td>
                    <td class="cookie-list__cell">${escapeHtml(cookie.type)}</td>
                  </tr>`
                  )
                  .join('')}
              </tbody>
            </table>
          </div>
        </section>`;
    });

    if (!html) {
      html = `<p class="cookie-list__empty">${escapeHtml(
        labels.empty || 'No cookies were detected on this page yet.'
      )}</p>`;
    }

    groups.innerHTML = html;
    groups.hidden = false;
    if (scanning) scanning.hidden = true;
  }

  function init(root) {
    const config = readConfig(root);
    const run = () => render(root, config, collectCookies(config));
    run();

    // Apps like Clarity can inject after first paint
    window.setTimeout(run, 1200);
    window.setTimeout(run, 3000);
  }

  function boot() {
    document.querySelectorAll('[data-cookie-scanner]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
